import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response } from '@ui/src';
import { ClientRange, Turnover } from '../../../types';
import { individualClientsRiskApi } from '../services';
import ROUTES from '../../../../routes';

export interface IndividualClientsRiskVolumesListState {
  values?: Turnover[];
  selectedList: string[];
  activeState?: boolean;
}

const initialState: IndividualClientsRiskVolumesListState = {
  values: undefined,
  selectedList: [],
  activeState: true,
};

export const individualClientsRiskVolumesListSlice = createSlice({
  name: 'individualClientsRiskVolumesList',
  initialState,
  reducers: {
    setActiveState: (state, action: PayloadAction<boolean>) => {
      state.activeState = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      individualClientsRiskApi.endpoints.getIndividualVolumes.matchFulfilled,
      (state, action: PayloadAction<Response<ClientRange>>) => {
        const monthlyTurnover = action.payload.data.edges
          .map((item) => item.node)
          .sort((a, b) => a.from - b.from);

        state.values = monthlyTurnover.map((item) => ({
          name: '',
          active: item.active,
          from: item.from,
          id: item.id,
          riskCategory: '-',
          riskScore: item.riskScore,
          type: item.type,
          url: generatePath(ROUTES.INDIVIDUAL_CLIENTS_RISK_VOLUMES_EDIT, {
            itemId: item.id,
          }),
        }));
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.createIndividualVolumes.matchFulfilled,
      (state, action: PayloadAction<{ data: ClientRange }>) => {
        let nextRanges: Turnover[] = [];

        if (state.values) {
          nextRanges = ([] as Turnover[]).concat(state.values);
        }

        nextRanges.push({
          name: '',
          active: action.payload.data.active,
          from: action.payload.data.from,
          id: action.payload.data.id,
          riskCategory: '-',
          riskScore: action.payload.data.riskScore,
          type: action.payload.data.type,
          url: generatePath(ROUTES.INDIVIDUAL_CLIENTS_RISK_VOLUMES_EDIT, {
            itemId: action.payload.data.id,
          }),
        });

        state.values = nextRanges.sort((a, b) => a.from - b.from);
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.updateIndividualVolumes.matchFulfilled,
      (state, action: PayloadAction<{ data: ClientRange }>) => {
        if (state.values) {
          state.values = state.values.map((item) => {
            let result = { ...item };

            if (item.id === action.payload.data.id) {
              result = {
                name: '',
                active: action.payload.data.active,
                from: action.payload.data.from,
                id: action.payload.data.id,
                riskCategory: '-',
                riskScore: action.payload.data.riskScore,
                type: action.payload.data.type,
                url: generatePath(ROUTES.INDIVIDUAL_CLIENTS_RISK_VOLUMES_EDIT, {
                  itemId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.batchDeleteIndividualVolumes
        .matchFulfilled,
      (state, action: any) => {
        if (state.values) {
          state.values = state.values.filter(
            (item) => item.id !== action.meta.arg.originalArgs,
          );
        }
      },
    );
  },
});

export const { setActiveState } = individualClientsRiskVolumesListSlice.actions;

export default individualClientsRiskVolumesListSlice.reducer;
